/**
 * GET {vitacapeRestUrl}consumer/dashboard/   (/api/v3/consumer/dashboard/)
 *
 * API response types + one pure mapper that turns the response into what the existing
 * Health Dashboard UI already uses. No values are created or calculated here
 * (BMI and BMI status are shown exactly as the API returns them).
 */

/** Fields exactly as the API returns them. Note the API spells it `weigthtUnit`. */
export interface DashboardProfileApi {
  dob?: string | null;
  height?: string | number | null;
  heightUnit?: string | null;
  weight?: string | number | null;
  weigthtUnit?: string | null;
  weightUnit?: string | null;       // tolerated in case the backend ever fixes the spelling
  bmi?: string | number | null;
  bmiStatus?: string | null;
  bloodGroup?: string | null;
}

export interface DashboardAllergyApi {
  allergicTo?: string | null;
  allergicReaction?: string[] | string | null;   // an array today; may hold several reactions
  severity?: string | null;
  comment?: string | null;
  created?: string | null;
  msg?: string | null;
}

export interface DashboardMedicationApi {
  name?: string | null;
  strength?: string | number | null;
  unit?: string | null;
  time?: string | null;
  id?: string | number | null;           // internal: never shown
}

/** Every vitals object is one reading. value2 / value3 are 0 when unused (e.g. Weight). */
export interface DashboardVitalApi {
  id?: string | number | null;
  name?: string | null;
  tag?: string | null;
  value1?: string | number | null;
  value2?: string | number | null;
  value3?: string | number | null;
  valueUnit1?: string | null;
  valueUnit2?: string | null;
  valueUnit3?: string | null;
  readingDateTime?: string | null;       // 'MM-dd-yyyy hh:mm a'
  risk?: string | null;                  // '' | 'HIGH' | 'CRITICAL'
  msg?: string | null;
}

export interface DashboardInsuranceApi {
  insuranceCompany?: string | null;
  planName?: string | null;
  status?: string | null;
  policyNumber?: string | null;          // not shown in the compact card
  memberId?: string | null;
  groupNumber?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  comment?: string | null;
  created?: string | null;
  id?: string | null;
  msg?: string | null;
}

export interface DashboardImmunizationApi {
  vaccineName?: string | null;
  adminDate?: string | null;             // 'MM-dd-yyyy'
  nextDueDate?: string | null;           // '' when not set
  detail?: string | null;
  created?: string | null;
  id?: string | null;
  msg?: string | null;
}

export interface DashboardFamilyHistoryApi {
  conditionName?: string | null;
  relationship?: string[] | string | null;
  comment?: string | null;
  created?: string | null;
  msg?: string | null;
}

export interface DashboardResponse {
  error: boolean;
  data?: {
    profile?: DashboardProfileApi | null;
    insurance?: DashboardInsuranceApi[] | null;
    immunization?: DashboardImmunizationApi[] | null;
    allergies?: DashboardAllergyApi[] | null;
    familyHistory?: DashboardFamilyHistoryApi[] | null;
    medications?: DashboardMedicationApi[] | null;
    vitals?: DashboardVitalApi[] | null;
  } | null;
}

/** Profile fields as the template already reads them (profileData.height, .weigthtUnit, ...). */
export interface DashboardProfileView {
  dob: string;
  height: string;
  heightUnit: string;
  weight: string;
  weigthtUnit: string;     // same (misspelled) key the template already uses
  weightUnit: string;      // correctly spelled alias for new code
  bmi: string;
  bmiStatus: string;
  bloodGroup: string;
}

export interface AllergyView {
  allergicTo: string;
  reactions: string[];     // always an array: zero, one or many
  severity: string;
  comment: string;
  created: string;
  msg: string;
}

export interface MedicationView {
  name: string;
  dose: string;            // 'strength unit', e.g. '25 IU'
  time: string;
}

export interface VitalView {
  name: string;
  reading: string;         // only the populated values, each with its unit
  tag: string;
  readingDateTime: string;
  risk: string;            // upper-case: '' | 'HIGH' | 'CRITICAL'
  msg: string;
}

export interface InsuranceView {
  planName: string;
  company: string;
  status: string;
}

export interface ImmunizationView {
  vaccineName: string;
  adminDate: string;       // 'MM-dd-yyyy' as returned
  nextDueDate: string;
  detail: string;
}

export interface FamilyHistoryView {
  conditionName: string;
  relationships: string[]; // always an array
  comment: string;
  created: string;
  msg: string;
}

export interface DashboardView {
  profile: DashboardProfileView;
  insuranceCount: number;
  immunizationCount: number;
  allergyCount: number;
  familyHistoryCount: number;
  allergies: AllergyView[];
  medications: MedicationView[] | null;   // null = the response has no `medications` key (callers keep their own source)
  vitals: VitalView[];
  familyHistory: FamilyHistoryView[];
  insurance: InsuranceView[];
  immunization: ImmunizationView[];
}

/** null / undefined / 'null' / 'undefined' / 'NaN' become '' so they never reach the screen. */
function clean(value: unknown): string {
  if (value === null || value === undefined) { return ''; }
  const s = String(value).trim();
  return (s === 'null' || s === 'undefined' || s === 'NaN') ? '' : s;
}

function reactionsOf(value: unknown): string[] {
  const list = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : []);
  return list.map(clean).filter(r => r.length > 0);
}

function listOf<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * 'value unit' for every populated slot, e.g. Weight -> '58 kg', Blood Pressure -> '122 mm Hg / 111 mm Hg · 123 bpm'.
 * value2 / value3 equal to 0 are the API's "unused" placeholder and are skipped; value1 is always kept.
 * Slots that share a unit with the previous one are joined with ' / ', others with ' · '.
 */
function readingOf(v: DashboardVitalApi | null | undefined): string {
  const slots: Array<[unknown, unknown]> = [[v?.value1, v?.valueUnit1], [v?.value2, v?.valueUnit2], [v?.value3, v?.valueUnit3]];
  let out = '';
  let prevUnit = '';
  slots.forEach(([raw, rawUnit], i) => {
    const val = clean(raw);
    if (!val || (i > 0 && Number(val) === 0)) { return; }
    const unit = clean(rawUnit);
    const part = unit ? `${val} ${unit}` : val;
    out = out ? out + (unit && unit === prevUnit ? ' / ' : ' · ') + part : part;
    prevUnit = unit;
  });
  return out;
}

/** Returns null when the response is an error or has no `data`, so the caller can show its error state. */
export function mapDashboardResponse(res: DashboardResponse | null | undefined): DashboardView | null {
  if (!res || res.error === true || !res.data) { return null; }
  const d = res.data;
  const p = d.profile || {};
  const weightUnit = clean(p.weigthtUnit) || clean(p.weightUnit);

  const allergies = listOf(d.allergies).map(a => ({
    allergicTo: clean(a?.allergicTo),
    reactions: reactionsOf(a?.allergicReaction),
    severity: clean(a?.severity),
    comment: clean(a?.comment),
    created: clean(a?.created),
    msg: clean(a?.msg)
  }));

  return {
    profile: {
      dob: clean(p.dob),
      height: clean(p.height),
      heightUnit: clean(p.heightUnit),
      weight: clean(p.weight),
      weigthtUnit: weightUnit,
      weightUnit,
      bmi: clean(p.bmi),
      bmiStatus: clean(p.bmiStatus),
      bloodGroup: clean(p.bloodGroup)
    },
    insuranceCount: listOf(d.insurance).length,
    immunizationCount: listOf(d.immunization).length,
    allergyCount: allergies.length,
    familyHistoryCount: listOf(d.familyHistory).length,
    allergies,
    medications: Array.isArray(d.medications)
      ? d.medications.map(m => ({
          name: clean(m?.name),
          dose: [clean(m?.strength), clean(m?.unit)].filter(s => s.length > 0).join(' '),
          time: clean(m?.time)
        }))
      : null,
    vitals: listOf(d.vitals).map(v => ({
      name: clean(v?.name),
      reading: readingOf(v),
      tag: clean(v?.tag),
      readingDateTime: clean(v?.readingDateTime),
      risk: clean(v?.risk).toUpperCase(),
      msg: clean(v?.msg)
    })),
    familyHistory: listOf(d.familyHistory).map(f => ({
      conditionName: clean(f?.conditionName),
      relationships: reactionsOf(f?.relationship),
      comment: clean(f?.comment),
      created: clean(f?.created),
      msg: clean(f?.msg)
    })),
    insurance: listOf(d.insurance).map(i => ({
      planName: clean(i?.planName),
      company: clean(i?.insuranceCompany),
      status: clean(i?.status)
    })),
    immunization: listOf(d.immunization).map(v => ({
      vaccineName: clean(v?.vaccineName),
      adminDate: clean(v?.adminDate),
      nextDueDate: clean(v?.nextDueDate),
      detail: clean(v?.detail)
    }))
  };
}

// ---------------------------------------------------------------------------------------------
// GET {cofferRestUrl}consumer/v3/dashboard/documents?tag=health  (Health Documents card)
// ---------------------------------------------------------------------------------------------

export interface DashboardDocApi {
  id?: string | null;
  name?: string | null;
  created?: string | null;      // created_docs items
  expiration?: string | null;   // expiry_docs items (relative text or a date, shown as returned)
}

export interface DashboardDocumentGroupApi {
  citizenship?: string | null;  // 'citizen_primary' | 'citizen_second' | 'citizen_third'
  country?: string | null;
  created_docs?: DashboardDocApi[] | null;
  expiry_docs?: DashboardDocApi[] | null;
}

export interface DashboardDocumentsResponse {
  error: boolean;
  data?: { documents?: DashboardDocumentGroupApi[] | null } | null;
}

export interface HealthDocumentItem {
  id: string;
  name: string;
  created: string;
  expiration: string;
}

export interface HealthDocumentsView {
  country: string;
  created: HealthDocumentItem[];
  expiring: HealthDocumentItem[];
}

/**
 * Keeps ONLY the `citizen_primary` group (never documents[0]: the API order must not decide).
 * Returns null for an error / missing `data` (card shows its error state); a response without a
 * primary group gives empty lists (card shows its empty state).
 */
export function mapHealthDocuments(res: DashboardDocumentsResponse | null | undefined): HealthDocumentsView | null {
  if (!res || res.error === true || !res.data) { return null; }
  const primary = listOf(res.data.documents).find(g => g?.citizenship === 'citizen_primary');
  const toItem = (d: DashboardDocApi | null | undefined): HealthDocumentItem => ({
    id: clean(d?.id),
    name: clean(d?.name),
    created: clean(d?.created),
    expiration: clean(d?.expiration)
  });
  const usable = (d: HealthDocumentItem) => d.id.length > 0 && d.name.length > 0;   // the id is needed to open the preview
  return {
    country: clean(primary?.country),
    created: listOf(primary?.created_docs).map(toItem).filter(usable),
    expiring: listOf(primary?.expiry_docs).map(toItem).filter(usable)
  };
}